import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "parent" | "student" | "school_admin" | "merchant" | "superadmin";

type Profile = { id: string; full_name: string | null; role: AppRole | null; school_id: string | null };

type AuthContextValue = {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isSuperadmin: boolean;
  tenantId: string | null;
  tenantSlug: string | null;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  profile: null,
  session: null,
  loading: true,
  signOut: async () => {},
  isSuperadmin: false,
  tenantId: null,
  tenantSlug: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      if (data.session) {
        currentUserIdRef.current = data.session.user?.id ?? null;
      } else {
        setLoading(false);
      }
    }).catch(() => {
      if (!mounted) return;
      setSession(null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (!newSession) {
        currentUserIdRef.current = null;
        setProfile(null);
        setLoading(false);
      } else {
        const newUserId = newSession.user?.id ?? null;
        const userIdChanged = currentUserIdRef.current !== newUserId;
        currentUserIdRef.current = newUserId;
        // Only re-trigger loading on a genuine user switch.
        // SIGNED_IN fires on every page reload (after getSession) and
        // TOKEN_REFRESHED fires ~every 60s — both with the same user id.
        // Re-setting loading=true in those cases would freeze the app
        // because the profile effect (keyed on user id) won't re-run.
        if (userIdChanged) {
          setLoading(true);
        }
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setProfile(null);
      return;
    }
    let active = true;
    const timeout = setTimeout(() => {
      if (!active) return;
      setProfile({
        id: session.user.id,
        full_name: session.user.user_metadata?.full_name ?? null,
        role: null,
        school_id: null,
      });
      setLoading(false);
    }, 2000);

    Promise.all([
      supabase.from("profiles").select("id, full_name, school_id").eq("id", session.user.id).maybeSingle(),
      supabase.from("user_roles").select("role, school_id").eq("user_id", session.user.id).limit(1).maybeSingle(),
    ]).then(([profileRes, roleRes]) => {
      if (!active) return;
      clearTimeout(timeout);
      if (profileRes.data) {
        setProfile({
          id: profileRes.data.id,
          full_name: profileRes.data.full_name,
          role: (roleRes.data?.role as AppRole) ?? null,
          school_id: roleRes.data?.school_id ?? profileRes.data.school_id ?? null,
        });
      } else {
        setProfile({
          id: session.user.id,
          full_name: session.user.user_metadata?.full_name ?? null,
          role: (roleRes.data?.role as AppRole) ?? null,
          school_id: roleRes.data?.school_id ?? null,
        });
      }
      if (active) setLoading(false);
    }).catch(() => {
      if (!active) return;
      clearTimeout(timeout);
      setProfile({
        id: session.user.id,
        full_name: session.user.user_metadata?.full_name ?? null,
        role: null,
        school_id: null,
      });
      setLoading(false);
    });

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [session?.user?.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  const isSuperadmin = profile?.role === "superadmin";
  const tenantId = profile?.school_id ?? null;
  const tenantSlug = profile?.school_id ?? null;

  return (
    <AuthContext.Provider
      value={{ user: session?.user ?? null, profile, session, loading, signOut, isSuperadmin, tenantId, tenantSlug }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
